import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

/**
 * The single day cell of the monthly attendance matrix, and the swatch in its
 * legend -- one component so the two can never drift apart.
 *
 * Badge is the wrong primitive here: its `rounded-full px-2.5` footprint is far
 * wider than a day column. Tones reuse the semantic bg/fg token pairs from
 * tokens.css, so light and dark both work with no new tokens.
 */
const statusChipVariants = cva(
  // overflow-hidden is load-bearing, not cosmetic: a label wider than the chip
  // (a long leave code) would otherwise spill across the neighbouring day cells.
  // align-middle is load-bearing too: as an inline-flex box the chip aligns on the
  // baseline, and an empty chip (an off day has no label) has no in-flow content to
  // take a baseline from, so the browser synthesises one from its bottom edge. That
  // parked empty chips ~3px above the lettered ones in the same row. Aligning on the
  // box middle instead makes placement independent of whether there is a label.
  "relative inline-flex items-center justify-center overflow-hidden rounded-[4px] px-0.5 align-middle font-semibold leading-none tabular-nums",
  {
    variants: {
      tone: {
        present: "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]",
        absent: "bg-[var(--color-error-bg)] text-[var(--color-error-fg)]",
        // Worked, but short of the shift (or never checked out) -- the timesheet report.
        warning: "bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]",
        leave: "bg-[var(--color-info-bg)] text-[var(--color-info-fg)]",
        // Half a working day, half leave -- split down the diagonal so the cell
        // reads as "partly both" at a glance rather than as a third status.
        halfDay:
          "bg-[linear-gradient(135deg,var(--color-success-bg)_50%,var(--color-info-bg)_50%)] text-[var(--color-info-fg)]",
        holiday: "bg-surface-700 text-white",
        off: "bg-surface-100 text-surface-400",
        none: "bg-transparent text-surface-300",
      },
      size: {
        compact: "h-6 w-6 text-[10px]",
        legend: "h-5 w-5 text-[10px]",
        // Wide enough for an hours figure such as "10.5" (the timesheet report).
        wide: "h-6 w-9 text-[10px]",
      },
    },
    defaultVariants: { tone: "none", size: "compact" },
  }
);

export interface StatusChipProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusChipVariants> {
  /** A correction request for this day was rejected. Drawn as a corner flag over
   *  the real status rather than replacing it, so the day's actual state stays visible. */
  marker?: boolean | undefined;
  /** This day has a correction request awaiting a decision ("pending") or already
   *  approved ("approved"). A dot on the top edge, so the day's own status chip is
   *  still readable underneath -- approval state is about the request, not the day. */
  dot?: "pending" | "approved" | undefined;
}

function StatusChipImpl({ className, tone, size, marker, dot, children, ...props }: StatusChipProps) {
  return (
    <span className={cn(statusChipVariants({ tone, size }), className)} {...props}>
      {children}
      {marker && (
        <span
          aria-hidden
          className="absolute right-0 top-0 h-0 w-0 border-l-[5px] border-t-[5px] border-l-transparent border-t-[var(--color-warning-accent)]"
        />
      )}
      {dot && (
        <span
          aria-hidden
          className={cn(
            "absolute left-1/2 top-[2px] size-1.5 -translate-x-1/2 rounded-full ring-1 ring-black/10",
            dot === "pending" ? "bg-[var(--color-warning-accent)]" : "bg-[var(--color-success)]"
          )}
        />
      )}
    </span>
  );
}

/**
 * Memoised: the attendance matrix renders one chip per employee per day -- several
 * hundred at a time -- and each one otherwise re-runs cva and twMerge on every parent
 * render. All props are primitives, so a shallow compare is both safe and effective.
 */
const StatusChip = React.memo(StatusChipImpl);
StatusChip.displayName = "StatusChip";

export { StatusChip, statusChipVariants };
